import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { signOut, sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mail, RefreshCw, ArrowRight } from 'lucide-react';

export default function VerifyEmail() {
  const { user, isLoading, emailVerified, isSuperadmin } = useAuth();
  const [resendStatus, setResendStatus] = useState('');
  const [checking, setChecking] = useState(false);

  // Already verified or superadmin — go straight to dashboard
  useEffect(() => {
    if (!isLoading && user && (emailVerified || isSuperadmin)) {
      window.location.href = '/author/dashboard';
    }
  }, [isLoading, user, emailVerified, isSuperadmin]);

  // Not logged in — go to login
  useEffect(() => {
    if (!isLoading && !user) {
      window.location.href = '/author/login';
    }
  }, [isLoading, user]);

  // Poll for verification every 5 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          await user.getIdToken(true);
          window.location.href = '/author/dashboard';
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const handleResend = useCallback(async () => {
    if (!user) return;
    setResendStatus('sending');
    try {
      await sendEmailVerification(user);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  }, [user]);

  const handleCheckNow = useCallback(async () => {
    if (!user) return;
    setChecking(true);
    try {
      await user.reload();
      if (user.emailVerified) {
        await user.getIdToken(true);
        window.location.href = '/author/dashboard';
      }
    } catch { /* ignore */ }
    setChecking(false);
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100/30 p-4">
      <Card className="w-full max-w-lg border-slate-200 shadow-lg">
        <CardContent className="p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-teal-100">
            <Mail className="h-8 w-8 text-teal-600" />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-slate-900">Check your email</h1>
          <p className="mb-2 text-slate-600">
            We've sent a verification link to <strong>{user?.email}</strong>.
          </p>
          <p className="mb-6 text-sm text-slate-500">
            Click the link in the email to verify your account.
            This page will update automatically once verified.
          </p>

          <div className="flex flex-col items-center gap-3">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleCheckNow}
              disabled={checking}
            >
              <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
              {checking ? 'Checking...' : 'I\'ve verified — check now'}
            </Button>

            <button
              onClick={handleResend}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              className="text-sm text-teal-600 hover:text-teal-700 disabled:text-slate-400"
            >
              {resendStatus === 'sending' ? 'Sending...' :
               resendStatus === 'sent' ? 'Verification email sent!' :
               resendStatus === 'error' ? 'Failed to send — try again' :
               'Resend verification email'}
            </button>

            <div className="mt-3 flex items-center gap-4">
              <button
                onClick={() => { window.location.href = '/author/dashboard'; }}
                className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
              >
                Skip to dashboard <ArrowRight className="h-3 w-3" />
              </button>
              <button
                onClick={() => signOut(auth).then(() => { window.location.href = '/author/login'; })}
                className="text-sm text-slate-400 hover:text-slate-600"
              >
                Sign out
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
